import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, AlertTriangle, User, MapPin, Calendar, Pill, AlertCircle, Sparkles, Users, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaydayDisplay = () => {
  const navigate = useNavigate();
  const [emergencyInfo, setEmergencyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmergencyInfo();
  }, []);

  const fetchEmergencyInfo = async () => {
    try {
      const response = await fetch(`${API}/emergency-info`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setEmergencyInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch emergency info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasAnyInfo = emergencyInfo && (
    emergencyInfo.name || 
    emergencyInfo.emergency_contacts?.some(c => c.name || c.phone)
  );

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Emergency Header - Highly Visible */}
      <header className="bg-red-600 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-all"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-white animate-pulse" />
            <div>
              <h1 className="text-white text-xl font-bold">EMERGENCY INFO</h1>
              <p className="text-white/80 text-sm">Show to Emergency Worker</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 pb-24">
        <div className="max-w-4xl mx-auto space-y-4">
          
          {!hasAnyInfo ? (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-2xl p-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-white text-lg font-semibold mb-2">No Emergency Info Set Up</h2>
              <p className="text-white/60 mb-4">Please add your emergency information in Settings → Mayday Settings</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-yellow-500 text-black font-semibold rounded-full hover:bg-yellow-400 transition-all"
              >
                Go to Settings
              </button>
            </div>
          ) : (
            <>
              {/* Quick Summary Card - Large and Visible */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{emergencyInfo.name || 'Name not set'}</h2>
                    <p className="text-gray-600 text-lg">Age: {emergencyInfo.age || 'Not set'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span>DOB: {emergencyInfo.birthdate || 'Not set'}</span>
                </div>
                
                <div className="flex items-start gap-2 text-gray-700">
                  <MapPin className="w-5 h-5 mt-0.5" />
                  <span>{emergencyInfo.address || 'Address not set'}</span>
                </div>
              </div>

              {/* Emergency Contacts - Tap to Call */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-600" />
                  Emergency Contacts
                </h3>
                <div className="space-y-4">
                  {emergencyInfo.emergency_contacts?.length > 0 ? (
                    emergencyInfo.emergency_contacts.map((contact, index) => (
                      <div key={index} className="bg-gray-50 rounded-xl p-4">
                        <p className="font-semibold text-gray-900 text-lg">{contact.name || 'Contact ' + (index + 1)}</p>
                        <p className="text-gray-600 text-sm mb-3">{contact.relationship || 'Relationship not set'}</p>
                        
                        <div className="flex gap-2">
                          {contact.phone ? (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition-all"
                            >
                              <Phone className="w-5 h-5" />
                              Call
                            </a>
                          ) : (
                            <div className="flex-1 flex items-center justify-center gap-2 bg-gray-300 text-gray-500 py-3 rounded-xl font-semibold">
                              <Phone className="w-5 h-5" />
                              No Phone
                            </div>
                          )}
                          {contact.email ? (
                            <a
                              href={`mailto:${contact.email}`}
                              className="flex-1 flex items-center justify-center gap-2 bg-blue-500 text-white py-3 rounded-xl font-semibold hover:bg-blue-600 transition-all"
                            >
                              <Mail className="w-5 h-5" />
                              Email
                            </a>
                          ) : (
                            <div className="flex-1 flex items-center justify-center gap-2 bg-gray-300 text-gray-500 py-3 rounded-xl font-semibold">
                              <Mail className="w-5 h-5" />
                              No Email
                            </div>
                          )}
                        </div>
                        
                        {contact.phone && (
                          <p className="text-center text-gray-500 text-sm mt-2">{contact.phone}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No emergency contacts set</p>
                  )}
                </div>
              </div>

              {/* Medical Information - ALWAYS SHOW */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Pill className="w-5 h-5 text-red-600" />
                  Medical Information
                </h3>
                
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Medical Conditions</p>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-xl">
                    {emergencyInfo.medical_conditions || 'None listed'}
                  </p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm font-semibold text-red-600 uppercase mb-1">⚠️ Allergies</p>
                  <p className="text-red-800 font-medium">
                    {emergencyInfo.allergies || 'None listed'}
                  </p>
                </div>
              </div>

              {/* Trigger Points - ALWAYS SHOW - Important for De-escalation */}
              <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  ⚠️ AVOID - Trigger Points
                </h3>
                <p className="text-orange-900 text-lg">
                  {emergencyInfo.trigger_points || 'No trigger points listed'}
                </p>
                <p className="text-orange-700 text-sm mt-2 italic">
                  Things that may cause stress, anxiety, or escalation
                </p>
              </div>

              {/* Connection Points - ALWAYS SHOW - Helpful for Calming */}
              <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  ✨ HELPS - Connection Points
                </h3>
                <p className="text-green-900 text-lg">
                  {emergencyInfo.connection_points || 'No connection points listed'}
                </p>
                <p className="text-green-700 text-sm mt-2 italic">
                  Topics, activities, or approaches that help calm and connect
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default MaydayDisplay;
